import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,

  useToast,
  useColorModeValue,
  Icon,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
} from '@chakra-ui/react';
import { 
  FiPlus, FiMinus,
  FiSearch, 
  FiEdit, 
  FiTrash2, 
  FiEye, 
  FiMoreVertical,
  FiDownload,
  FiMail,
  FiDollarSign
} from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import PDFDownloadModal from '../components/PDFDownloadModal';
import { statusColor, statusKeyPath } from '../utils/status';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { tr, enUS, de } from 'date-fns/locale';

const Proposals = () => {
  const { t, currentLanguage } = useLanguage();
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const parentRowBg0 = useColorModeValue('white', 'gray.800');
  const parentRowBg1 = useColorModeValue('gray.50', 'gray.700');
  const childRowBg0 = useColorModeValue('gray.100', 'gray.600');
  const childRowBg1 = useColorModeValue('gray.200', 'gray.500');
  const evalBg = useColorModeValue('gray.50','gray.700');
  const parentTdPy = 2;
  const childTdPy = 1;
  const navigate = useNavigate();
  
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const { onClose: onNewClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [defaultCurrency] = useState('EUR');
  const [newProposal, setNewProposal] = useState({
    title: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    total_amount: '',
    notes: '',
    valid_until: '',
    status: 'draft'
  });

  const getDateLocale = () => {
    switch (currentLanguage) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };

  const { data: proposals, isLoading } = useQuery(['proposals', page, pageSize, searchTerm, statusFilter], () =>
    api.get(`/api/proposals?limit=${pageSize}&offset=${page*pageSize}&q=${encodeURIComponent(searchTerm)}&status=${statusFilter||''}`)
      .then(res => res.data.data.proposals)
  );

  // Load default currency from settings
  // useEffect(() => {
  //   (async () => {
  //     try {
  //       const { data } = await api.get('/api/settings/currency.default');
  //       const val = data?.data?.value || 'EUR';
  //       setDefaultCurrency(val);
  //     } catch (_) { setDefaultCurrency('EUR'); }
  //   })();
  // }, []);

  const currencySymbol = (code) => {
    const map = { EUR: '€', USD: '$', GBP: '£', TRY: '₺' };
    return map[code?.toUpperCase?.() || ''] || code || '€';
  };

  const { data: groupedData } = useQuery(['proposals_grouped', page, pageSize, searchTerm, statusFilter], () =>
    api.get(`/api/proposals/grouped?limit=${pageSize}&offset=${page*pageSize}&q=${encodeURIComponent(searchTerm)}&status=${statusFilter||''}`)
      .then(res => res.data.data.groups),
    { keepPreviousData: true }
  );

  const [pdfModalProposalId, setPdfModalProposalId] = useState(null);
  const downloadProposalPdf = (id) => setPdfModalProposalId(id);

  // Excel download goes straight to the file — no preview modal, no slider
  // overrides, no persisted state. Pulls the filename from the response's
  // Content-Disposition header (server already sets it via buildProposalPdfFilename).
  const downloadProposalExcel = async (id) => {
    try {
      const res = await api.get(
        `/api/proposals/${id}/excel?lang=${currentLanguage}`,
        { responseType: 'blob' }
      );
      const cd = res.headers && res.headers['content-disposition'];
      let filename = `proposal_${id}.xlsx`;
      if (cd) {
        const star = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        if (star) {
          try { filename = decodeURIComponent(star[1]); } catch (_) { /* keep fallback */ }
        } else {
          const plain = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
          if (plain) filename = plain[1].trim();
        }
      }
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: t('common.error') || 'Error', status: 'error' });
    }
  };

  // Parent satırları için grup eşleşmesi: parent_id -> group
  const multiGroups = (groupedData || []).filter(g => Array.isArray(g.versions) && g.versions.length > 0)
    .map(g => ({
      ...g,
      versions: (g.versions||[]).slice().sort((a,b)=> (Number(b.version_number||0) - Number(a.version_number||0)) || (new Date(b.created_at) - new Date(a.created_at)))
    }));
  const headIdToGroup = new Map(multiGroups.map(g => [ g.parent_id, g ]));
  const childIds = new Set(multiGroups.flatMap(g => (g.versions || []).map(v => v.id)));
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const ns = new Set(prev);
      if (ns.has(key)) ns.delete(key); else ns.add(key);
      return ns;
    });
  };

  const createProposalMutation = useMutation(
    (data) => api.post('/api/proposals', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('proposals');
        toast({
          title: t('common.success'),
          description: t('proposals.created_success'),
          status: 'success',
        });
        onNewClose();
        setNewProposal({
          title: '',
          client_name: '',
          client_email: '',
          client_phone: '',
          total_amount: '',
          notes: '',
          valid_until: '',
          status: 'draft'
        });
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.response?.data?.message || t('messages.server_error'),
          status: 'error',
        });
      },
    }
  );

  const deleteProposalMutation = useMutation(
    (id) => api.delete(`/api/proposals/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('proposals');
        toast({
          title: t('common.success'),
          description: t('proposals.deleted_success'),
          status: 'success',
        });
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.response?.data?.message || t('messages.server_error'),
          status: 'error',
        });
      },
    }
  );

  const statusChangeMutation = useMutation(
    ({ id, status }) => api.put(`/api/proposals/${id}/status`, { status }),
    {
      onSuccess: (data, variables) => {
        // Optimistic update: flat list
        try {
          queryClient.setQueryData(['proposals', page, pageSize, searchTerm, statusFilter], (old) => {
            if (!Array.isArray(old)) return old;
            return old.map(p => (p.id === variables.id ? { ...p, status: variables.status } : p));
          });
          // Optimistic update: grouped list
          queryClient.setQueryData(['proposals_grouped', page, pageSize, searchTerm, statusFilter], (groups) => {
            if (!Array.isArray(groups)) return groups;
            return groups.map(g => ({
              ...g,
              versions: (g.versions || []).map(v => (v.id === variables.id ? { ...v, status: variables.status } : v))
            }));
          });
        } catch (_) {}
        queryClient.invalidateQueries('proposals');
        queryClient.invalidateQueries('proposals_grouped');
        toast({
          title: t('common.success'),
          description: `Proposal status changed to ${variables.status.toLocaleUpperCase('en-US')}`,
          status: 'success',
        });
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.response?.data?.message || t('messages.server_error'),
          status: 'error',
        });
      },
    }
  );

  const sortedProposals = [...(proposals||[])].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getStatusColor = statusColor;



  const handleDeleteProposal = (id) => {
    if (window.confirm(t('messages.confirm_delete'))) {
      deleteProposalMutation.mutate(id);
    }
  };

  const handleStatusChange = (id, status) => {
    statusChangeMutation.mutate({ id, status });
  };

  const handleViewProposal = async (proposal) => {
    try {
      // Detaydan tam alan seti (email/phone vs.)
      const res = await api.get(`/api/proposals/${proposal.id}`);
      const full = res?.data?.data?.proposal || {};
      setSelectedProposal({ ...proposal, ...full });
    } catch (_) {
      setSelectedProposal(proposal);
    }
    onViewOpen();
  };



  // Transfer & New Version modals (UI-friendly)
  const [transferModal, setTransferModal] = useState({ open: false, proposal: null, search: '', clientId: '' });
  const [versionModal, setVersionModal] = useState({ open: false, proposal: null, note: '' });
  const { data: modalClients } = useQuery(['clients_lookup', transferModal.search], () =>
    api.get(`/api/users/clients?q=${encodeURIComponent(transferModal.search||'')}`).then(r => r.data.data.clients),
    { enabled: transferModal.open }
  );

  // Invoices & Payments modal state
  const [invModal, setInvModal] = useState({ open: false, proposalId: null });
  const [invoiceCreateForm, setInvoiceCreateForm] = useState({ invoice_type: 'advance', mode: 'percent', value: 50, due_date: '', notes: '', items: [] });
  // Duplicate project modal
  const [duplicateModal, setDuplicateModal] = useState({ open: false, base: null, type: 'copy', inflation: 5 });

  const { data: invSummary } = useQuery(
    ['proposal_invoices', invModal.proposalId],
    () => api.get(`/api/proposals/${invModal.proposalId}/invoices`).then(r => r.data.data),
    { enabled: invModal.open && Boolean(invModal.proposalId) }
  );
  const [addPaymentState, setAddPaymentState] = useState({}); // {invoiceId:{amount,payment_date,method}}

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">{t('proposals.title')}</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={() => { try { localStorage.setItem('sidebar_collapsed','true'); } catch {}; window.dispatchEvent(new CustomEvent('sidebar:set-collapsed', { detail: true })); navigate('/proposals/new'); }}>
          {t('proposals.new')}
        </Button>
      </Flex>

      {/* Filters */}
      <Card bg={bgColor} border="1px" borderColor={borderColor} mb={6}>
        <CardBody>
          <Flex gap={4} wrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            
            <Select
              maxW="200px"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              placeholder={t('proposals.status')}
            >
              <option value="draft">{t('proposals.status.draft')}</option>
              <option value="sent">{t('proposals.status.sent')}</option>
              <option value="accepted">{t('proposals.status.accepted')}</option>
              <option value="rejected">{t('proposals.status.rejected')}</option>
            </Select>

            <Select
              maxW="200px"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="created_at">{t('common.created_at')}</option>
              <option value="title">{t('proposals.title')}</option>
              <option value="client_name">{t('proposals.customer')}</option>
              <option value="total_amount">{t('proposals.total')}</option>
            </Select>

            <Select
              maxW="150px"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">{t('common.desc')}</option>
              <option value="asc">{t('common.asc')}</option>
            </Select>
          </Flex>
        </CardBody>
      </Card>

      {/* Proposals - Single unified table; groups expand inline via + icon */}
      <Card bg={bgColor} border="1px" borderColor={borderColor}>
        <CardBody>
          {isLoading ? (
            <Text textAlign="center" py={8}>{t('common.loading')}</Text>
          ) : (
            <Table variant="simple" size="sm" width="100%" tableLayout="fixed">
                  <Thead>
                    <Tr>
                      <Th w="24px"></Th>
                      <Th w="90px">{t('proposals.number')}</Th>
                      <Th w="25%">{t('proposals.title')}</Th>
                      <Th w="20%">{t('proposals.customer')}</Th>
                      <Th w="100px">{t('proposals.date')}</Th>
                      <Th w="120px">{t('proposals.author')}</Th>
                      <Th w="120px">{t('proposals.total')}</Th>
                      <Th w="100px">{t('proposals.status')}</Th>
                      <Th w="120px">{t('common.actions')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(() => {
                      // Parent list: for grouped ones, show latest version as main row
                      const parentProposals = (sortedProposals || []).filter(p => !childIds.has(p.id));
                      return parentProposals.flatMap((proposal, parentIdx) => {
                        const group = headIdToGroup.get(proposal.id);
                        const isGroup = Boolean(group);
                        const isExpanded = isGroup && expandedGroups.has(group.key);
                        const parentBg = parentIdx % 2 === 0 ? parentRowBg0 : parentRowBg1;
                        const rows = [];
                        rows.push(
                          <Tr key={proposal.id} bg={parentBg}>
                            <Td py={parentTdPy} w="24px">
                              {isGroup ? (
                                <IconButton aria-label="toggle" size="xs" variant="ghost" icon={isExpanded ? <FiMinus /> : <FiPlus />} onClick={() => toggleGroup(group.key)} />
                              ) : null}
                            </Td>
                            {(() => { 
                              const display = (isGroup ? [proposal, ...(group.versions||[])] : [proposal])
                                .slice()
                                .sort((a,b)=> (Number(b.version_number||0) - Number(a.version_number||0)) || (new Date(b.created_at) - new Date(a.created_at)))[0];
                              return (
                                <>
                                  <Td fontWeight="medium" py={parentTdPy} w="90px" whiteSpace="nowrap">#{display.id}</Td>
                                  <Td py={parentTdPy}>
                                    <Text fontWeight="medium">{(display?.title || (group && group.title) || proposal.title)} {display?.version_number ? `(v${display.version_number})` : ''}</Text>
                                  </Td>
                                  <Td py={parentTdPy}>
                                    <VStack align="start" spacing={1}>
                                      <Text fontWeight="medium">{(display.client_company || display.client_name) || (group && (group.client_company || group.client_name)) || (proposal.client_company || proposal.client_name)}</Text>
                                      <Text fontSize="sm" color="gray.500">{display.client_email || proposal.client_email}</Text>
                                    </VStack>
                                  </Td>
                                  <Td py={parentTdPy}>
                                    <Text fontSize="sm">{format(new Date(display.created_at), 'MMM dd, yyyy', { locale: getDateLocale() })}</Text>
                                  </Td>
                                  <Td py={parentTdPy}><Text fontSize="sm">{display.created_by_name || '-'}</Text></Td>
                                  <Td py={parentTdPy}>
                                    <Text fontWeight="medium" color="green.600">{currencySymbol(display.currency || defaultCurrency)}{Number(display.total_amount||0).toLocaleString()}</Text>
                                  </Td>
                                  <Td py={parentTdPy}>
                                    <Badge colorScheme={getStatusColor(display.status)}>{t(statusKeyPath(display.status))}</Badge>
                                  </Td>
                                  <Td py={parentTdPy}>
                                    <HStack spacing={2}>
                                      <Tooltip label={t('common.view')}>
                                        <IconButton size="sm" icon={<FiEye />} variant="ghost" onClick={() => handleViewProposal(display)} />
                                      </Tooltip>
                                      <Tooltip label={t('common.edit')}>
                                        <IconButton as={RouterLink} to={`/proposals/${display.id}/edit`} size="sm" icon={<FiEdit />} variant="ghost" />
                                      </Tooltip>
                                      <Menu>
                                        <Tooltip label={t('common.actions')}><MenuButton as={IconButton} size="sm" icon={<FiMoreVertical />} variant="ghost" /></Tooltip>
                                        <MenuList>
                                          <MenuItem icon={<FiDollarSign />} onClick={() => setInvModal({ open: true, proposalId: display.id })}>
                                            {t('invoices.title', { defaultValue: 'Invoices & Payments' })}
                                          </MenuItem>
                                          <MenuItem icon={<FiMail />}>{t('common.send')}</MenuItem>
                                          <MenuItem icon={<FiDownload />} onClick={() => downloadProposalPdf(display.id)}>{t('common.download_pdf', { defaultValue: 'Download as PDF' })}</MenuItem>
                                          <MenuItem icon={<FiDownload />} onClick={() => downloadProposalExcel(display.id)}>{t('common.download_excel', { defaultValue: 'Download as Excel' })}</MenuItem>
                                          <MenuDivider />
                                          {user && (user.role === 'admin' || user.role === 'manager') && (
                                            <>
                                              <MenuItem onClick={() => handleStatusChange(display.id, 'in_progress')}>{t('proposals.set_as_in_progress')||'Set as IN PROGRESS'}</MenuItem>
                                              <MenuItem onClick={() => handleStatusChange(display.id, 'commissioned')}>{t('proposals.set_as_commissioned')||'Set as COMMISSIONED'}</MenuItem>
                                              <MenuItem onClick={() => handleStatusChange(display.id, 'rejected')}>{t('proposals.set_as_rejected')||'Set as REJECTED'}</MenuItem>
                                              <MenuDivider />
                                              <MenuItem onClick={() => setDuplicateModal({ open: true, base: display })}>{t('proposals.duplicate_project')||'Duplicate project'}</MenuItem>
                                            </>
                                          )}
                                          {user && user.role === 'admin' && (
                                            <>
                                              <MenuDivider />
                                              <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => handleDeleteProposal(display.id)}>
                                                {t('proposals.delete') || 'Delete Proposal'}
                                              </MenuItem>
                                            </>
                                          )}
                                        </MenuList>
                                      </Menu>
                                    </HStack>
                                  </Td>
                                </>
                              );
                            })()}
                          </Tr>
                        );
                        if (isExpanded) {
                          const allVersions = (isGroup ? [proposal, ...(group.versions||[])] : [proposal])
                            .slice()
                            .sort((a,b)=> (Number(b.version_number||0) - Number(a.version_number||0)) || (new Date(b.created_at) - new Date(a.created_at)));
                          const latestId = allVersions[0]?.id;
                          const children = allVersions.filter(v => v.id !== latestId);
                          for (let ci = 0; ci < children.length; ci++) {
                            const v = children[ci];
                            const childBg = (ci % 2 === 0) ? childRowBg0 : childRowBg1;
                            rows.push(
                              <Tr key={`child-${v.id}`} bg={childBg}>
                                <Td py={childTdPy}></Td>
                                <Td py={childTdPy}></Td>
                                <Td py={childTdPy}>
                                  <Text fontWeight="medium">↳ {v.title || group.title}</Text>
                                  <Text fontSize="xs" color="gray.500">v{v.version_number}</Text>
                                </Td>
                                <Td py={childTdPy}>
                                  <VStack align="start" spacing={0}>
                                    <Text fontSize="sm">{v.client_company || v.client_name || group.client_name}</Text>
                                  </VStack>
                                </Td>
                                <Td py={childTdPy}>{format(new Date(v.created_at), 'MMM dd, yyyy', { locale: getDateLocale() })}</Td>
                                <Td py={childTdPy}><Text fontSize="sm">{v.created_by_name || '-'}</Text></Td>
                                <Td py={childTdPy}>{currencySymbol(v.currency || defaultCurrency)}{Number(v.total_amount||0).toLocaleString()}</Td>
                                <Td py={childTdPy}><Badge colorScheme={getStatusColor(v.status)}>{t(statusKeyPath(v.status))}</Badge></Td>
                                <Td py={childTdPy}>
                                  <HStack spacing={2}>
                                    <IconButton as={RouterLink} to={`/proposals/${v.id}/edit`} size="sm" icon={<FiEdit />} variant="ghost" aria-label="edit" />
                                    <IconButton size="sm" icon={<FiEye />} variant="ghost" aria-label="view" onClick={() => handleViewProposal(v)} />
                                  </HStack>
                                </Td>
                              </Tr>
                            );
                          }
                        }
                        return rows;
                      });
                    })()}
                  </Tbody>
            </Table>
          )}
          <HStack justify="space-between" mt={4}>
            <HStack>
              <Button size="sm" onClick={() => setPage(p => Math.max(0, p-1))} isDisabled={page===0}>{t('common.prev')}</Button>
              <Button size="sm" onClick={() => setPage(p => p+1)} isDisabled={(proposals?.length||0) < pageSize}>{t('common.next')}</Button>
            </HStack>
            <HStack>
              <Text fontSize="sm">{t('common.rows')}</Text>
              <Select size="sm" value={pageSize} onChange={(e)=>{setPage(0); setPageSize(Number(e.target.value));}} maxW="80px">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Select>
            </HStack>
          </HStack>
        </CardBody>
      </Card>

      {/* New Proposal Modal kaldırıldı; ayrı sayfaya yönlendiriliyor */}

      {/* View Proposal Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('proposals.view')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedProposal && (
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="bold">{t('proposals.number')}:</Text>
                  <Text>#{selectedProposal.id}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">{t('proposals.title')}:</Text>
                  <Text>{selectedProposal.title}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">{t('proposals.customer')}:</Text>
                  <Text>{selectedProposal.client_company || selectedProposal.client_name}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">{t('auth.email')}:</Text>
                  <Text>{selectedProposal.client_email}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">{t('common.phone')}:</Text>
                  <Text>{selectedProposal.client_phone}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">{t('proposals.total')}:</Text>
                  <Text color="green.600" fontWeight="bold">
                    {currencySymbol(selectedProposal?.currency || defaultCurrency)}{selectedProposal?.total_amount?.toLocaleString()}
                  </Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">{t('proposals.status')}:</Text>
                  <Badge colorScheme={getStatusColor(selectedProposal.status)}>
                    {t(`proposals.status.${selectedProposal.status}`)}
                  </Badge>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">{t('proposals.date')}:</Text>
                  <Text>
                    {format(new Date(selectedProposal.created_at), 'MMM dd, yyyy', { locale: getDateLocale() })}
                  </Text>
                </HStack>
                
                {selectedProposal?.valid_until && (
                  <HStack justify="space-between">
                    <Text fontWeight="bold">{t('proposals.valid_until')}:</Text>
                    <Text>{selectedProposal?.valid_until}</Text>
                  </HStack>
                )}
                
                {/* Project evaluation */}
                <Box mt={2} p={3} bg={evalBg} borderRadius="md">
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text fontWeight="bold">{t('proposals.eval.selling_price', { defaultValue: 'Selling price / project value' })}</Text>
                      <Text>{currencySymbol(selectedProposal?.currency || defaultCurrency)}{Number(selectedProposal?.total_amount||0).toLocaleString()}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="bold">{t('proposals.eval.total_purchase', { defaultValue: 'Total purchase price' })}</Text>
                      <Text>{currencySymbol(selectedProposal?.currency || defaultCurrency)}{Number(selectedProposal?.purchase_total||0).toLocaleString()}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="bold">{t('proposals.eval.margin_pct', { defaultValue: 'Margin %' })}</Text>
                      <Text>{(() => {
                        try {
                          const firstItem = (selectedProposal?.items || [])[0];
                          if (firstItem) {
                            let jd = firstItem.json_data;
                            if (typeof jd === 'string') { try { jd = JSON.parse(jd); } catch(_) { jd = null; } }
                            const marginVal = (jd && jd.margin !== undefined ? Number(jd.margin) : (firstItem.margin !== undefined ? Number(firstItem.margin) : null));
                            if (typeof marginVal === 'number' && !isNaN(marginVal)) {
                              const pct = (marginVal >= 1 ? (marginVal - 1) : marginVal) * 100;
                              return `${pct.toFixed(0)}%`;
                            }
                          }
                        } catch(_) {}
                        // Fallback: hesaplanan oran (satış-maliyet)
                        const sell = Number(selectedProposal?.total_amount||0);
                        const buy = Number(selectedProposal?.purchase_total||0);
                        if (sell <= 0) return '0%';
                        const pct = ((sell - buy) / sell) * 100;
                        return `${pct.toFixed(0)}%`;
                      })()}</Text>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onViewClose}>
              {t('common.close')}
            </Button>
            <Button colorScheme="brand" onClick={() => { onViewClose(); navigate(`/proposals/${selectedProposal.id}/edit`); }}>
              {t('common.edit')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Invoices & Payments Modal (global, proposal list üzerinden) */}
      <Modal isOpen={invModal.open} onClose={() => setInvModal({ open: false, proposalId: null })} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{'Invoices & Payments'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {invSummary ? (
              <VStack align="stretch" spacing={6}>
                <Card><CardBody>
                  <HStack justify="space-between">
                    <Text fontWeight="bold">{invSummary.proposal?.title}</Text>
                    <HStack>
                      <Text color="gray.600">Total:</Text>
                      <Text fontWeight="bold">{(invSummary.summary?.proposal_total||0).toLocaleString()}</Text>
                    </HStack>
                  </HStack>
                  <HStack mt={2} spacing={8}>
                    <HStack><Text color="gray.600">Invoiced:</Text><Text fontWeight="bold">{(invSummary.summary?.total_invoiced||0).toLocaleString()}</Text></HStack>
                    <HStack><Text color="gray.600">Paid:</Text><Text fontWeight="bold">{(invSummary.summary?.total_paid||0).toLocaleString()}</Text></HStack>
                    <HStack><Text color="gray.600">Remaining to invoice:</Text><Text fontWeight="bold">{(invSummary.summary?.remaining_to_invoice||0).toLocaleString()}</Text></HStack>
                    <HStack><Text color="gray.600">Outstanding:</Text><Text fontWeight="bold">{(invSummary.summary?.outstanding_balance||0).toLocaleString()}</Text></HStack>
                  </HStack>
                </CardBody></Card>

                <Card><CardBody>
                  <HStack justify="space-between" mb={3}>
                    <Text fontWeight="bold">Create Invoice</Text>
                    <HStack>
                      <Select size="sm" value={invoiceCreateForm.invoice_type} onChange={(e)=> setInvoiceCreateForm(prev=>({...prev, invoice_type: e.target.value}))} maxW="160px">
                        <option value="advance">Advance</option>
                        <option value="completion">Completion</option>
                        <option value="extra">Extra</option>
                      </Select>
                      <Select size="sm" value={invoiceCreateForm.mode} onChange={(e)=> setInvoiceCreateForm(prev=>({...prev, mode: e.target.value}))} isDisabled={invoiceCreateForm.invoice_type==='extra'} maxW="120px">
                        <option value="percent">Percent</option>
                        <option value="fixed">Fixed</option>
                      </Select>
                      <Input size="sm" type="number" step="0.01" value={invoiceCreateForm.value} onChange={(e)=> setInvoiceCreateForm(prev=>({...prev, value: e.target.value}))} placeholder="Value" maxW="140px" />
                      <Input size="sm" type="date" value={invoiceCreateForm.due_date} onChange={(e)=> setInvoiceCreateForm(prev=>({...prev, due_date: e.target.value}))} maxW="160px" />
                      <Input size="sm" value={invoiceCreateForm.notes} onChange={(e)=> setInvoiceCreateForm(prev=>({...prev, notes: e.target.value}))} placeholder="Notes" maxW="240px" />
                                              <Button size="sm" colorScheme="green" onClick={async ()=>{
                          try {
                            const payload = { proposal_id: invModal.proposalId, ...invoiceCreateForm };
                            await api.post('/api/invoices/create-from-proposal', payload);
                            setInvoiceCreateForm({ invoice_type: 'advance', mode:'percent', value:50, due_date:'', notes:'', items:[] });
                            toast({ title: t('common.success'), status:'success' });
                            queryClient.invalidateQueries(['proposal_invoices', invModal.proposalId]);
                            queryClient.invalidateQueries('proposals'); // Refresh proposals list to update status
                          } catch (e) {
                            toast({ title: t('common.error'), description: e.response?.data?.error || 'Error', status:'error' });
                          }
                        }}>Create</Button>
                    </HStack>
                  </HStack>

                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Number</Th>
                        <Th>Type</Th>
                        <Th isNumeric>Amount</Th>
                        <Th>Status</Th>
                        <Th>Date</Th>
                        <Th w="320px">Payments</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(invSummary.invoices||[]).map(inv => (
                        <Tr key={inv.id}>
                          <Td>{inv.invoice_number}</Td>
                          <Td>{inv.invoice_type}</Td>
                          <Td isNumeric>{(inv.invoiced_amount || inv.total_amount || 0).toLocaleString()}</Td>
                          <Td>{inv.status}</Td>
                          <Td>{new Date(inv.created_at).toLocaleDateString()}</Td>
                          <Td>
                            <HStack>
                              <Input size="xs" placeholder="Amount" value={addPaymentState[inv.id]?.amount || ''} onChange={(e)=> setAddPaymentState(prev=>({...prev, [inv.id]: { ...(prev[inv.id]||{}), amount: e.target.value }}))} maxW="100px" />
                              <Input size="xs" type="date" value={addPaymentState[inv.id]?.payment_date || ''} onChange={(e)=> setAddPaymentState(prev=>({...prev, [inv.id]: { ...(prev[inv.id]||{}), payment_date: e.target.value }}))} maxW="140px" />
                              <Input size="xs" placeholder="Method" value={addPaymentState[inv.id]?.method || ''} onChange={(e)=> setAddPaymentState(prev=>({...prev, [inv.id]: { ...(prev[inv.id]||{}), method: e.target.value }}))} maxW="120px" />
                              <Button size="xs" onClick={async ()=>{
                                try {
                                  const s = addPaymentState[inv.id] || {}; const amt = Number(s.amount||0);
                                  if (!(amt>0)) { toast({ title: t('common.error'), description: 'Amount > 0', status:'error' }); return; }
                                  await api.post(`/api/invoices/${inv.id}/payments`, { amount: amt, payment_date: s.payment_date || null, method: s.method || null });
                                  setAddPaymentState(prev=>({...prev,[inv.id]:{}}));
                                  toast({ title: t('common.success'), status:'success' });
                                } catch (e) {
                                  toast({ title: t('common.error'), description: e.response?.data?.error || 'Error', status:'error' });
                                }
                              }}>Add</Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </CardBody></Card>
              </VStack>
            ) : (
              <Text>{t('common.loading')||'Loading...'}</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={()=> setInvModal({ open: false, proposalId: null })}>{t('common.close')||'Close'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Transfer Client Modal */}
      <Modal isOpen={transferModal.open} onClose={() => setTransferModal({ open: false, proposal: null, search: '', clientId: '' })} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('proposals.transfer_client', {defaultValue: 'Transfer Client'})}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>{t('common.search')}</FormLabel>
                <Input value={transferModal.search} onChange={(e) => setTransferModal({ ...transferModal, search: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('clients.title') || 'Client'}</FormLabel>
                <Select size="sm" value={transferModal.clientId} onChange={(e) => setTransferModal({ ...transferModal, clientId: e.target.value })}>
                  <option value="">{t('common.select')}</option>
                  {modalClients?.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setTransferModal({ open: false, proposal: null, search: '', clientId: '' })}>{t('common.cancel')}</Button>
            <Button colorScheme="brand" onClick={async () => {
              if (!transferModal.clientId) return;
              try {
                await api.put(`/api/proposals/${transferModal.proposal.id}/transfer`, { client_id: Number(transferModal.clientId) });
                queryClient.invalidateQueries('proposals');
                toast({ title: t('common.success'), description: t('settings.updated_success'), status: 'success' });
                setTransferModal({ open: false, proposal: null, search: '', clientId: '' });
              } catch (e) {
                toast({ title: t('common.error'), description: t('messages.server_error'), status: 'error' });
              }
            }}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* New Version Modal */}
      <Modal isOpen={versionModal.open} onClose={() => setVersionModal({ open: false, proposal: null, note: '' })} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('proposals.new_version', {defaultValue: 'New Version'})}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>{t('proposals.version_note', {defaultValue: 'Version Note'})}</FormLabel>
              <Textarea value={versionModal.note} onChange={(e) => setVersionModal({ ...versionModal, note: e.target.value })} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setVersionModal({ open: false, proposal: null, note: '' })}>{t('common.cancel')}</Button>
            <Button colorScheme="brand" onClick={async () => {
              try {
                await api.post(`/api/proposals/${versionModal.proposal.id}/new-version`, { version_note: versionModal.note || '' });
                queryClient.invalidateQueries('proposals');
                toast({ title: t('common.success'), description: t('proposals.created_success'), status: 'success' });
                setVersionModal({ open: false, proposal: null, note: '' });
              } catch (e) {
                toast({ title: t('common.error'), description: t('messages.server_error'), status: 'error' });
              }
            }}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Duplicate Project Modal */}
      <Modal isOpen={duplicateModal.open} onClose={() => setDuplicateModal({ open: false, base: null, type: 'copy', inflation: 5 })} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('proposals.duplicate_project')||'Duplicate project'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Select value={duplicateModal.type} onChange={(e)=> setDuplicateModal(prev=>({...prev, type: e.target.value}))}>
                <option value="copy">{t('proposals.duplicate.copy')||'Carbon copy'}</option>
                <option value="inflation">{t('proposals.duplicate.inflation')||'Copy with inflation adjustment'}</option>
                <option value="current_prices">{t('proposals.duplicate.current_prices')||'Use current unit prices'}</option>
              </Select>
              {duplicateModal.type === 'inflation' && (
                <HStack>
                  <Text>{t('proposals.duplicate.inflation_pct')||'Inflation %'}</Text>
                  <Input type="number" step="0.1" value={duplicateModal.inflation} onChange={(e)=> setDuplicateModal(prev=>({...prev, inflation: Number(e.target.value)}))} />
                </HStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={()=> setDuplicateModal({ open:false, base:null, type:'copy', inflation:5 })}>{t('common.cancel')}</Button>
            <Button colorScheme="brand" onClick={async ()=>{
              try {
                const base = duplicateModal.base; if (!base) return;
                const payload = { mode: duplicateModal.type, inflation_pct: duplicateModal.inflation };
                await api.post(`/api/proposals/${base.id}/duplicate`, payload);
                queryClient.invalidateQueries('proposals');
                toast({ title: t('common.success'), status:'success' });
                setDuplicateModal({ open:false, base:null, type:'copy', inflation:5 });
              } catch (e) {
                toast({ title: t('common.error'), description: t('messages.server_error'), status:'error' });
              }
            }}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <PDFDownloadModal
        isOpen={pdfModalProposalId !== null}
        onClose={() => setPdfModalProposalId(null)}
        proposalId={pdfModalProposalId}
      />
    </Box>
  );
};

export default Proposals;

